import * as React from 'react';

import type { BatchUpdateAndSubmitGradesMutationInjectedProps } from 'bundles/grading/api/BatchUpdateAndSubmitGradesMutationWrapper';
import BatchUpdateAndSubmitGradesMutationWrapper from 'bundles/grading/api/BatchUpdateAndSubmitGradesMutationWrapper';
import type { GradeVisibilityMutationInjectedProps } from 'bundles/grading/api/GradeVisibilityMutationWrapper';
import GradeVisibilityMutationWrapper from 'bundles/grading/api/GradeVisibilityMutationWrapper';

export type MutationInjectedProps = BatchUpdateAndSubmitGradesMutationInjectedProps &
  GradeVisibilityMutationInjectedProps;

type Props = {
  courseId: string;
  children: (mutations: MutationInjectedProps) => React.ReactElement | null;
};

/**
 * Mutation wrapper around SGA Grading Page.
 * Provides access to grading mutation functions and mutation status.
 */
const GradingMutationWrapper = ({ courseId, children }: Props) => {
  // Mutation component runs `update` callback on mutation complete.
  // The callback receives access to cache, which allows to patch previously queried data.
  // Starting apollo-client v3, the `MutationUpdaterFn` receives 3 parameters, including variables used to call the mutation.
  // `MutationUpdaterFunction` in apollo-client v2 misses variables. Hence we retrieve those parameters from the mutation response.
  // https://github.com/apollographql/apollo-client/issues/8506
  //
  // Currently, each feature mutation is responsible to handle their own `update` callback actions and pass along their
  // mutations object

  return (
    <BatchUpdateAndSubmitGradesMutationWrapper>
      {(batchUpdateAndSubmitGradesMutations) => (
        <GradeVisibilityMutationWrapper courseId={courseId}>
          {(gradeVisibilityMutations) => {
            const mutations = {
              ...batchUpdateAndSubmitGradesMutations,
              ...gradeVisibilityMutations,
            };
            return children(mutations);
          }}
        </GradeVisibilityMutationWrapper>
      )}
    </BatchUpdateAndSubmitGradesMutationWrapper>
  );
};

export default GradingMutationWrapper;
